import { useState } from "react";
import { PencilIcon } from "lucide-react";
import { useDebounce } from "use-debounce";

import { useUserSheet } from "./userSheetContext";

import { Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Command, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { useUpdateProfileUser } from "@/hooks/mutations/useUpdateProfileUser";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useUpdateSetCandidacyCommute } from "@/hooks/updateSets/useUpdateSetCandidacyCommute";
import { userSheetViewStore } from "@/stores/userSheetView";
import { useGetPlaces } from "@/hooks/queries/useGetPlaces";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { locations } from "@/lib/locations";
import { novaClient } from "@/lib/trpc";

const LOCATION_QUERY_DEBOUNCE_MS = 250;

// Hover-revealed location editor in the sheet header; opens a places search.
export const UserSheetHeaderLocation = () => {
  /* Context */
  const { candidacyId, isReadOnly, jobId, workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const stafferId = useGetCurrentUser()?.id;
  const { mutate: recomputeCommute } = useUpdateSetCandidacyCommute();
  const setIsCommuteRefreshing = userSheetViewStore(
    (s) => s.setIsCommuteRefreshing,
  );
  const sheetDataQuery = useGetCandidacySheetData({ jobId, userId: workerId });
  // Update
  const { mutate: updateProfileUser } = useUpdateProfileUser();

  /* Cache Updates */
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const novaClientUtils = novaClient.useUtils();

  /* React Hooks */
  const [isEditing, setIsEditing] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");
  const [debouncedLocationQuery] = useDebounce(
    locationQuery,
    LOCATION_QUERY_DEBOUNCE_MS,
  );
  const placesData = useGetPlaces(debouncedLocationQuery).data;

  /* Helpers */
  const location = sheetDataQuery.data?.profile.location;

  /* Handlers */
  const setOptimisticLocation = (newLocation: string) => {
    novaClientUtils.internal.user.active.getCandidacySheetData.setData(
      { jobId, userId: workerId },
      (previous) =>
        previous
          ? {
              ...previous,
              profile: { ...previous.profile, location: newLocation },
            }
          : previous,
    );
  };

  const handleSuccess = (selectedLocation: string) => {
    // The nova sheet-data cache was already updated optimistically above.
    // The recompute endpoint computes synchronously and returns the fresh
    // commute; the hook writes it into the caches, and the flag keeps the
    // metric spinning until it lands.
    if (candidacyId) {
      recomputeCommute(
        { candidacyId, location: selectedLocation },
        {
          onSettled: () => {
            setIsCommuteRefreshing(false);
          },
        },
      );
      setCandidacyInAllLists({ candidacyId, location: selectedLocation });
    } else {
      setIsCommuteRefreshing(false);
    }
  };

  const handleLocationSelect = (selectedLocation: string) => {
    if (!stafferId) return;

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const previousLocation = location || "";
    setOptimisticLocation(selectedLocation);

    const handleError = () => {
      setOptimisticLocation(previousLocation);
      setIsCommuteRefreshing(false);
    };
    setIsCommuteRefreshing(true);

    updateProfileUser(
      {
        stafferId,
        userId: workerId,
        data: { location: selectedLocation },
      },
      {
        onError: handleError,
        onSuccess: () => {
          handleSuccess(selectedLocation);
        },
      },
    );

    setIsEditing(false);
  };

  /* Render */
  const render = () => {
    if (isReadOnly) {
      return (
        <Text className="self-start text-sm font-normal leading-snug text-slate-600">
          {location ? locations.stripCountrySuffix(location) : "No location"}
        </Text>
      );
    }

    return (
      <Popover
        onOpenChange={(open) => {
          setIsEditing(open);
          // Reset on open, not close — clearing on close would blank the
          // list out from under the closing animation before it finishes.
          if (open) setLocationQuery("");
        }}
        open={isEditing}
      >
        <PopoverTrigger asChild>
          <Button
            className="group/location h-auto min-w-0 gap-1 self-start rounded-md p-0 text-slate-600 hover:bg-transparent hover:text-slate-950 data-[state=open]:text-slate-950"
            type="button"
            variant="ghost"
          >
            <Text className="text-sm font-normal leading-snug text-slate-600 group-hover/location:text-slate-950 group-data-[state=open]/location:text-slate-950">
              {location
                ? locations.stripCountrySuffix(location)
                : "Add location"}
            </Text>
            <PencilIcon
              className="hidden h-4 w-4 shrink-0 text-slate-400 group-hover/location:block group-data-[state=open]/location:block"
              strokeWidth={1.5}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 border-0 p-0">
          <Input
            className="h-8 text-slate-700"
            onChange={(e) => {
              setLocationQuery(e.target.value);
            }}
            placeholder="Begin typing a location..."
            value={locationQuery}
          />
          {locationQuery && placesData && placesData.length > 0 ? (
            <Command>
              <CommandList>
                {placesData.map((place) => (
                  <CommandItem
                    key={place}
                    onSelect={() => {
                      handleLocationSelect(place);
                    }}
                  >
                    {place}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          ) : null}
        </PopoverContent>
      </Popover>
    );
  };

  return render();
};
